import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../App';

const STEPS = ['Enter Details', 'Admin Verify', 'Place Finger', 'Done'];

export default function VoterRegistration() {
  const navigate = useNavigate();

  const [step,     setStep]     = useState(0);   // 0=form, 1=password, 2=scanning, 3=done
  const [name,     setName]     = useState('');
  const [voterId,  setVoterId]  = useState('');
  const [slot,     setSlot]     = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,      setLoading]      = useState(false);
  const [enrolled,     setEnrolled]     = useState(null);
  const [enrollStatus, setEnrollStatus] = useState('waiting');
  const pollRef    = useRef(null);
  const seenActive = useRef(false);   // true once ESP32 posts place_finger

  // ── Poll enrollment status while on step 2 ───────────────────
  useEffect(() => {
    if (step !== 2) return;
    setEnrollStatus('waiting');
    seenActive.current = false;

    pollRef.current = setInterval(async () => {
      try {
        const res  = await fetch(`${API_BASE}/api/enrollment-status`);
        const data = await res.json();
        const s    = data.status;

        // Mark that ESP32 has actually started this enrollment
        if (s === 'place_finger') seenActive.current = true;

        // Ignore stale "failed" from a previous run until ESP32 starts fresh
        if ((s === 'failed' || s === 'mismatch') && !seenActive.current) return;

        setEnrollStatus(s);
        if (s === 'done') {
          clearInterval(pollRef.current);
          setTimeout(() => setStep(3), 1500);
        }
        if (s === 'failed' || s === 'mismatch') {
          clearInterval(pollRef.current);
        }
      } catch { /* ignore network blips */ }
    }, 1000);
    return () => clearInterval(pollRef.current);
  }, [step]);

  // ── Clear a conflicting slot then retry enroll ───────────────
  const clearSlot = async () => {
    if (!password) { setError('Enter admin password first to clear the slot.'); return; }
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API_BASE}/api/voters/${slot}`, {
        method:  'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok) {
        // slot cleared — now re-run enrollment
        const res2  = await fetch(`${API_BASE}/api/enroll`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ password, slot: parseInt(slot, 10), voter_name: name.trim(), voter_id: voterId.trim() }),
        });
        const data2 = await res2.json();
        if (res2.ok) {
          setEnrolled({ name: name.trim(), slot: parseInt(slot, 10) });
          setStep(2);
        } else {
          setError(data2.error || 'Re-enrollment failed.');
        }
      } else {
        setError(data.error || 'Could not clear slot. Check password.');
      }
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 0: collect voter details ────────────────────────────
  const handleDetailsSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !slot) return;
    setStep(1);
  };

  // ── Step 1: admin password + send enroll request ─────────────
  const handleEnroll = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/enroll`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          password,
          slot:       parseInt(slot, 10),
          voter_name: name.trim(),
          voter_id:   voterId.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setEnrolled({ name: name.trim(), slot: parseInt(slot, 10) });
        setStep(2);
      } else {
        setError(data.error || 'Authorization failed. Check admin password.');
      }
    } catch {
      setError('Network error — is Flask server running?');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="screen">

      {/* ── Page title ────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <button className="back-btn" onClick={() => navigate('/')}>← முகப்பு | Home</button>
        <div className="section-title" style={{ marginTop: 12 }}>
          வாக்காளர் பதிவு | Voter Registration
        </div>
        <p className="section-subtitle">
          Register a new voter's fingerprint on the biometric sensor for the 2026 election.
        </p>
      </div>

      {/* ── Step indicator ────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 0,
        marginBottom: 32, overflowX: 'auto',
      }}>
        {STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 80 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: i <= step ? 'var(--tnec-navy)' : 'var(--border)',
                color: i <= step ? '#fff' : 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: '0.85rem',
                transition: 'all 0.3s',
              }}>
                {i < step ? '✓' : i + 1}
              </div>
              <span style={{
                fontSize: '0.7rem', fontWeight: i === step ? 700 : 400,
                color: i === step ? 'var(--tnec-navy)' : 'var(--text-muted)',
                whiteSpace: 'nowrap',
              }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, minWidth: 24,
                background: i < step ? 'var(--tnec-navy)' : 'var(--border)',
                marginBottom: 22, transition: 'background 0.3s',
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════
          STEP 0 — Voter Details Form
      ══════════════════════════════════════════════════ */}
      {step === 0 && (
        <div className="card" style={{ maxWidth: 520, margin: '0 auto' }}>
          <h3 style={{ color: 'var(--tnec-navy)', fontWeight: 800, marginBottom: 4 }}>
            👤 வாக்காளர் விவரங்கள் | Voter Details
          </h3>
          <p className="section-desc">
            Enter the voter's information and assign a fingerprint sensor slot (1–127).
          </p>

          <form onSubmit={handleDetailsSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">முழு பெயர் | Full Name *</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g. Rajan Murugesan"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">வாக்காளர் அடையாள எண் | Voter ID</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="e.g. TN/2026/001"
                  value={voterId}
                  onChange={e => setVoterId(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Sensor Slot (1–127) *</label>
                <input
                  className="form-input"
                  type="number"
                  placeholder="e.g. 1"
                  min="1"
                  max="127"
                  value={slot}
                  onChange={e => setSlot(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="alert alert-info" style={{ marginBottom: 0 }}>
              <span>ℹ️</span>
              <span>
                Each voter needs a unique slot number (1–127).
                The slot is stored in the fingerprint sensor memory.
              </span>
            </div>

            <button type="submit" className="btn btn-primary btn-lg btn-full">
              அடுத்து | Next →
            </button>
          </form>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          STEP 1 — Admin Password
      ══════════════════════════════════════════════════ */}
      {step === 1 && (
        <div className="card" style={{ maxWidth: 520, margin: '0 auto' }}>
          <h3 style={{ color: 'var(--tnec-navy)', fontWeight: 800, marginBottom: 4 }}>
            🔐 நிர்வாகி அங்கீகாரம் | Admin Authorization
          </h3>
          <p className="section-desc">
            Registering voter: <strong style={{ color: 'var(--tnec-navy)' }}>{name}</strong>
            &nbsp;— Slot <strong>{slot}</strong>
            <br />Enter the admin password to authorize this enrollment.
          </p>

          <form onSubmit={handleEnroll} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">கடவுச்சொல் | Admin Password *</label>
              <input
                className="form-input"
                type="password"
                placeholder="Admin password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoFocus
              />
            </div>

            {error && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className="alert alert-error" style={{ marginBottom: 0 }}>
                  <span>⚠️</span><span>{error}</span>
                </div>
                {error.toLowerCase().includes('already assigned') && (
                  <button
                    type="button"
                    className="btn btn-full"
                    style={{ background: '#c0392b', color: '#fff', fontWeight: 700 }}
                    onClick={clearSlot}
                    disabled={loading}
                  >
                    {loading ? 'Clearing…' : `🗑 Clear Slot ${slot} & Re-register`}
                  </button>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setStep(0); setError(''); }}
              >
                ← Back
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
              >
                {loading ? 'Authorizing…' : '✓ Authorize & Start Enrollment'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          STEP 2 — Live Enrollment Status
      ══════════════════════════════════════════════════ */}
      {step === 2 && enrolled && (() => {
        const STEPS_INFO = [
          { key: 'waiting',       icon: '⏳', label: 'Waiting for ESP32...',         sub: 'ESP32 will detect registration in ~2 seconds' },
          { key: 'place_finger',  icon: '👆', label: 'Place finger on sensor',       sub: 'Sensor LED is ON — press finger firmly', pulse: true },
          { key: 'remove_finger', icon: '✋', label: 'Remove finger',                sub: 'Lift your finger off the sensor' },
          { key: 'place_again',   icon: '👆', label: 'Place same finger again',      sub: 'Sensor LED is ON — press the same finger', pulse: true },
          { key: 'done',          icon: '✅', label: 'Enrolled successfully!',        sub: 'Redirecting...' },
          { key: 'mismatch',      icon: '❌', label: 'Fingers did not match',        sub: 'Re-register the voter and try again' },
          { key: 'failed',        icon: '❌', label: 'Enrollment failed',            sub: 'Re-register the voter and try again' },
        ];
        const current = STEPS_INFO.find(s => s.key === enrollStatus) || STEPS_INFO[0];
        const isError = enrollStatus === 'failed' || enrollStatus === 'mismatch';

        return (
          <div className="card" style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
            <h3 style={{ color: 'var(--tnec-navy)', fontWeight: 800, marginBottom: 4 }}>
              கைரேகை பதிவு | Fingerprint Enrollment
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 20 }}>
              {enrolled.name} — Slot {enrolled.slot}
            </p>

            <style>{`
              @keyframes pulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(27,58,107,0.35); }
                50%       { box-shadow: 0 0 0 20px rgba(27,58,107,0); }
              }
              @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>

            {/* Status icon */}
            <div style={{
              width: 110, height: 110, borderRadius: '50%',
              background: isError ? '#fff0f0' : 'var(--accent-lt)',
              border: `3px solid ${isError ? 'var(--error, #c0392b)' : 'var(--tnec-navy)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '2.8rem',
              animation: current.pulse ? 'pulse 1.2s ease-in-out infinite' : 'none',
              transition: 'all 0.4s',
            }}>
              {current.icon}
            </div>

            {/* Status text */}
            <div style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: '1.1rem', fontWeight: 700,
                color: isError ? 'var(--error, #c0392b)' : 'var(--tnec-navy)',
                marginBottom: 6,
              }}>
                {current.label}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                {current.sub}
              </div>
            </div>

            {/* Step progress dots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 }}>
              {['waiting', 'place_finger', 'remove_finger', 'place_again', 'done'].map((s, i) => {
                const order = ['waiting', 'place_finger', 'remove_finger', 'place_again', 'done'];
                const currentIdx = order.indexOf(enrollStatus);
                const done = i < currentIdx;
                const active = i === currentIdx;
                return (
                  <div key={s} style={{
                    width: active ? 28 : 10, height: 10, borderRadius: 5,
                    background: done || active ? 'var(--tnec-navy)' : 'var(--border)',
                    transition: 'all 0.3s',
                  }} />
                );
              })}
            </div>

            {isError && (
              <button
                className="btn btn-secondary btn-full"
                onClick={() => { setStep(0); setEnrolled(null); setEnrollStatus('waiting'); setError(''); setPassword(''); }}
              >
                ← Try Again
              </button>
            )}
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════
          STEP 3 — Registration Complete
      ══════════════════════════════════════════════════ */}
      {step === 3 && enrolled && (
        <div className="card" style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>

          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'var(--success-lt)',
            border: '3px solid var(--success)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '2.2rem',
          }}>✅</div>

          <h3 style={{ color: 'var(--success)', fontWeight: 800, fontSize: '1.3rem', marginBottom: 8 }}>
            பதிவு வெற்றிகரமாக முடிந்தது!
          </h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
            Voter Registration Successful
          </p>

          <div className="success-details" style={{ marginBottom: 20 }}>
            <div className="success-detail-row">
              <span className="detail-label">பெயர் | Name</span>
              <span className="detail-value" style={{ color: 'var(--tnec-navy)' }}>{enrolled.name}</span>
            </div>
            <div className="success-detail-row">
              <span className="detail-label">Sensor Slot</span>
              <span className="detail-value">#{enrolled.slot}</span>
            </div>
            <div className="success-detail-row">
              <span className="detail-label">நிலை | Status</span>
              <span className="detail-value text-success">Fingerprint Enrolled ✓</span>
            </div>
            <div className="success-detail-row">
              <span className="detail-label">வாக்களிக்கலாம் | Can Vote</span>
              <span className="detail-value text-success">Yes ✓</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              className="btn btn-primary"
              onClick={() => {
                setStep(0); setName(''); setVoterId('');
                setSlot(''); setPassword(''); setEnrolled(null);
              }}
            >
              + Register Another Voter
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/')}>
              முகப்பு | Home
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
